import { useEffect } from "react";
import { ArrowRight, Calendar, CheckCircle2, Code2, ExternalLink, Mail, MapPin, Sparkles } from "lucide-react";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";

const RELEASES = [
  {
    date: "12 October 2026",
    title: "Zynthoro.ai Announces Strategic Technology Partnership with J&K Advanced Technologies",
    summary: "Exploring advanced AI infrastructure, security and storage through controlled staging evaluation.",
    href: "https://www.prlog.org/13176044-zynthoroai-announces-strategic-technology-partnership-with-jk-advanced-technologies.html",
  },
  {
    date: "30 June 2026",
    title: "Dutch Female Founder Launches AI ERP Ecosystem — XPRIZE Nominee",
    summary: "The launch announcement introduced Zynthoro as a connected AI-native business platform for SMEs worldwide.",
    href: "https://www.prlog.org/13155163-dutch-female-founder-launches-ai-erp-ecosystem-xprize-nominee.html",
  },
];

const WORK = [
  "Build and evaluate reliable AI workflows across connected business modules.",
  "Improve context, retrieval and orchestration for our 4 AI specialists.",
  "Work with APIs, data models, testing and observability.",
  "Keep security, privacy and reliability part of every release.",
];
const FIT = [
  "Strong Python and applied AI or LLM experience.",
  "Experience with production APIs, data pipelines and testable systems.",
  "Clear documentation and the ability to work independently.",
  "A focus on useful products, not just impressive demos.",
];

function List({ items }) {
  return <ul className="mt-5 space-y-4">{items.map((item) => (
    <li key={item} className="flex gap-3 text-[14.5px] leading-relaxed text-black/65">
      <CheckCircle2 size={18} className="mt-0.5 shrink-0 text-[#202B60]" />
      <span>{item}</span>
    </li>
  ))}</ul>;
}

export default function CompanyInfoPage({ kind }) {
  const careers = kind === "careers";
  useEffect(() => {
    document.title = careers ? "Careers — Zynthoro" : "Press — Zynthoro";
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", careers
      ? "Join Zynthoro. Explore our first opening for an AI/ML Engineer."
      : "Official Zynthoro press releases, company news and media contact information.");
    window.scrollTo(0, 0);
  }, [careers]);

  return <>
    <Navbar />
    <main data-testid={careers ? "page-careers" : "page-press"}>
      <section style={{ background: "var(--zy-blue)" }} className="text-white">
        <div className="zy-container py-16 md:py-24">
          <p className="uppercase tracking-[0.18em] text-[12px]" style={{ color: "var(--zy-gold)" }}>
            {careers ? "Careers" : "Press"}
          </p>
          <h1 className="mt-4 text-4xl md:text-6xl font-bold tracking-tight max-w-4xl">
            {careers ? "Build what businesses will use next." : "The Zynthoro story. Ready to share."}
          </h1>
          <p className="mt-6 text-white/75 text-[16px] md:text-[18px] leading-relaxed max-w-2xl">
            {careers
              ? "We are building one connected platform for ambitious businesses worldwide. Small team. Big plans. Real responsibility from day 1."
              : "Official company news, partnership updates and media information from Zynthoro."}
          </p>
        </div>
      </section>

      {careers ? (
        <section className="bg-[#FAFBFD]">
          <div className="zy-container py-14 md:py-20 max-w-5xl">
            <div className="flex flex-wrap items-center gap-3 mb-8">
              <span className="inline-flex items-center gap-2 rounded-full bg-[#202B60] px-4 py-2 text-[12px] font-semibold uppercase tracking-[0.12em] text-white">
                <Sparkles size={14} style={{ color: "var(--zy-gold)" }} /> First opening
              </span>
              <span className="inline-flex items-center gap-2 text-[13px] text-black/55">
                <MapPin size={14} /> Remote · Europe preferred
              </span>
            </div>
            <article className="rounded-3xl bg-white ring-1 ring-black/[0.07] shadow-[0_24px_70px_-45px_rgba(32,43,96,0.55)] p-7 sm:p-10 md:p-12">
              <div className="flex items-start justify-between gap-6 flex-wrap">
                <div>
                  <p className="zy-eyebrow">Technology · AI</p>
                  <h2 className="mt-3 text-3xl md:text-4xl font-bold text-[#0A1628]">AI/ML Engineer</h2>
                </div>
                <Code2 size={36} className="text-[#202B60]" />
              </div>
              <p className="mt-6 text-[16px] leading-relaxed text-black/65 max-w-3xl">
                Help turn connected business data into practical AI that supports real work.
                You will strengthen the intelligence behind Zynthoro&apos;s 4 AI specialists
                and the workflows across our business platform.
              </p>
              <div className="grid md:grid-cols-2 gap-10 mt-10">
                <div><h3 className="text-[18px] font-bold text-[#0A1628]">What you&apos;ll work on</h3><List items={WORK} /></div>
                <div><h3 className="text-[18px] font-bold text-[#0A1628]">You might be a fit if you have</h3><List items={FIT} /></div>
              </div>
              <div className="mt-10 rounded-2xl bg-[#202B60] p-6 sm:p-8 text-white">
                <h3 className="text-[19px] font-bold">Interested?</h3>
                <p className="mt-2 text-white/70 text-[14.5px] leading-relaxed max-w-2xl">
                  Send your CV or LinkedIn profile, relevant work and a short note about what you would bring to Zynthoro.
                  Contract form, hours and compensation are discussed with suitable candidates.
                </p>
                <a className="mt-5 inline-flex items-center gap-2 rounded-lg px-5 py-3 font-semibold text-[#202B60]"
                  style={{ background: "var(--zy-gold)" }} data-testid="careers-apply"
                  href="mailto:hello@zynthoro.ai?subject=Application%20%E2%80%94%20AI%2FML%20Engineer">
                  Apply for this role <ArrowRight size={16} />
                </a>
              </div>
            </article>
            <p className="mt-8 text-center text-[13.5px] text-black/50">
              Not your role? Send a focused open application to{" "}
              <a className="font-semibold text-[#202B60] hover:underline"
                href="mailto:hello@zynthoro.ai?subject=Open%20application%20%E2%80%94%20Zynthoro">hello@zynthoro.ai</a>.
            </p>
          </div>
        </section>
      ) : (
        <section className="bg-[#FAFBFD]">
          <div className="zy-container py-14 md:py-20 max-w-5xl">
            <div className="flex items-end justify-between gap-6 flex-wrap mb-8">
              <div><p className="zy-eyebrow">Press releases</p><h2 className="zy-h2 mt-3">Latest company news.</h2></div>
              <a href="mailto:info@zynthoro.ai?subject=Press%20enquiry%20%E2%80%94%20Zynthoro"
                className="zy-btn-outline inline-flex items-center gap-2" data-testid="press-contact">
                <Mail size={16} /> Media enquiry
              </a>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {RELEASES.map((release) => (
                <article key={release.href} className="flex flex-col rounded-2xl bg-white p-7 sm:p-8 ring-1 ring-black/[0.07] shadow-sm">
                  <p className="inline-flex items-center gap-2 text-[12.5px] text-black/50"><Calendar size={14} /> {release.date}</p>
                  <h3 className="mt-4 text-[21px] leading-snug font-bold text-[#0A1628]">{release.title}</h3>
                  <p className="mt-4 text-[14.5px] leading-relaxed text-black/60 flex-1">{release.summary}</p>
                  <a href={release.href} target="_blank" rel="noopener noreferrer"
                    className="mt-6 inline-flex items-center gap-2 text-[13.5px] font-semibold text-[#202B60] hover:underline">
                    Read the original release <ExternalLink size={14} />
                  </a>
                </article>
              ))}
            </div>
            <div className="mt-10 rounded-2xl bg-[#202B60] p-7 sm:p-9 text-white">
              <h2 className="text-2xl font-bold">Media information</h2>
              <p className="mt-3 max-w-2xl text-white/70 leading-relaxed">
                For interviews, founder background, product information or brand materials, include your publication, subject and deadline.
              </p>
              <a href="mailto:info@zynthoro.ai?subject=Press%20enquiry%20%E2%80%94%20Zynthoro"
                className="mt-5 inline-flex items-center gap-2 font-semibold" style={{ color: "var(--zy-gold)" }}>
                info@zynthoro.ai <ArrowRight size={16} />
              </a>
            </div>
          </div>
        </section>
      )}
    </main>
    <Footer />
  </>;
}
